import { useNavigate } from "react-router-dom";
import styles from "./UsersAdministrationList.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { IUsersAdministrationUserModel } from "./models/usersAdministrationUserModel";
import { classNames } from "primereact/utils";
import {
  constructUserHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";

export const UsersAdministration = () => {
  const navigate = useNavigate();
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.userAdd,
  });

  const { data: users, isLoading } = useApiQuery<
    IUsersAdministrationUserModel[]
  >({
    queryKey: [endpointsMetadata.user.getAll],
    endpoint: endpointsMetadata.user.getAll,
    globalIsLoading: false,
  });

  const itemOnClick = (id?: number) => {
    if (id) {
      const href = constructUserHref(id);
      navigate(href);
    }
  };

  return (
    <ItemsList
      itemOnClick={itemOnClick}
      isLoading={isLoading}
      className={styles.root}
      title="Налични потребители"
      noItemsText="Няма намерени потребители."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <UserItem
            className={className}
            user={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            actionButtons={actionButtons}
          />
        );
      }}
      items={users ?? []}
      swipeLeftActions={[
        {
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да изтриете този потребител?",
          type: "other",
          method: "delete",
          endpoint: endpointsMetadata.user.deleteUser,
          iconType: IconType.DeleteIcon,
          validator: (item: IUsersAdministrationUserModel) => !item.isDeleted,
          invalidateKeys: [[endpointsMetadata.user.getAll]],
        },
        {
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да възстановите този потребител?",
          type: "other",
          method: "put",
          endpoint: endpointsMetadata.user.putUserBack,
          iconType: IconType.PutBackIcon,
          validator: (item: IUsersAdministrationUserModel) => item.isDeleted,
          invalidateKeys: [[endpointsMetadata.user.getAll]],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          iconType: IconType.EditIcon,
          endpoint: routePaths.userEdit,
          validator: (item: IUsersAdministrationUserModel) => !item.isDeleted,
        },
      ]}
      deletedFilter={{
        storageKey: "users",
        isDeleted: (item: IUsersAdministrationUserModel) => !!item.isDeleted,
        sectionTitle: "Изтрити потребители",
        toggleLabel: "Покажи изтритите потребители",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface IUserItemProps {
  className?: string;
  user?: IUsersAdministrationUserModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

const UserItem = ({
  className,
  user,
  isLoading,
  actionButtons,
}: IUserItemProps) => {
  return (
    <div
      id={"hello"}
      className={classNames(className, styles.item, {
        [styles.item_deleted]: user?.isDeleted,
      })}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          width={"70%"}
          value={
            <div className={styles.item_left_name}>
              <div className={styles.item_left_name}>
                {user?.firstName} {user?.lastName}{" "}
              </div>

              <span className={styles.item_left_name_id}>
                (ID: {user?.id}
                {user?.originalStudio && `, ${user.originalStudio}`})
              </span>
            </div>
          }
        />
        <SkeletonItem
          isLoading={isLoading}
          width={"51%"}
          value={
            <div className={styles.item_left_secondary_item}>
              {user?.email || "N/A"}
            </div>
          }
        />

        <SkeletonItem
          isLoading={isLoading}
          width={"60%"}
          value={
            <div className={styles.item_left_secondary_item}>
              {user?.phoneNumber || "N/A"}{" "}
              {user?.isUnreliable ? (
                <span className={styles.item_left_unreliable}>· НЕРЕДОВЕН</span>
              ) : (
                ""
              )}
            </div>
          }
        />
      </div>
      <div className={styles.item_right}>
        {actionButtons}
        <Icon type={IconType.RightChevronIcon} />
      </div>
    </div>
  );
};
